import * as React from "react";
import OtpInput from "react-otp-input";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTimeout } from "react-use";
import { clsx } from "clsx";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoginFormInputs } from "@/views/auth/login/types";

interface TwoFactorFormProps {
  isLoading: boolean;
  error: string | null;
  resendCode: () => void;
}

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ resendCode, isLoading, error }) => {
  const [isResendCodeEnabled, , resetIsResendCodeEnabled] = useTimeout(30000);
  const { control } = useFormContext<LoginFormInputs>();
  const code = useWatch({
    control,
    name: "code",
  });

  const handleResendCode = () => {
    resendCode();
    resetIsResendCodeEnabled();
  };

  return (
    <>
      <h2 className="mb-4 text-lg font-medium text-ui-paragraph-text">Enter your two-factor authentication code.</h2>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-center">
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <OtpInput
              skipDefaultStyles={true}
              value={code}
              numInputs={6}
              shouldAutoFocus={true}
              renderInput={props => (
                <input
                  {...props}
                  disabled={isLoading}
                  className="mx-1 block w-1/6 appearance-none rounded-md border border-input px-4 py-3 text-center placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-lg"
                />
              )}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="mt-6">
        <button
          type="submit"
          disabled={isLoading || (code || "").length !== 6}
          className="flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <CircleLoader /> Verifying...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </div>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleResendCode}
          className={clsx("text-sm font-medium text-primary", {
            "hover:underline": isResendCodeEnabled(),
          })}
          disabled={!isResendCodeEnabled()}
        >
          Resend code
        </button>
      </div>
    </>
  );
};
